import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getActivosPorEmpresa } from "../services/activos/activosData";
import { getVulnerabilidadPorEmpresa } from "../services/vulnerabilidades/vulnerabilidadesData";
import { getAmenazasPorEmpresa } from "../services/amenazas/amenazasData";
import { createAmenazaVulnerabilidad } from "../services/amenazas/amenazasVulnerabilidades/amenazasVulnerabilidadesData";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export const VulnerabilidadAmenaza = () => {
  const [rowData, setRowData] = useState({
    vulnerabilidad: null,
    amenazas: [],
  });
  const [activos, setActivos] = useState([]);
  const [vulnerabilidades, setVulnerabilidades] = useState([]);
  const [amenazas, setAmenazas] = useState([]);
  const [frecuenciaOptions] = useState([
    { label: "Muy Baja", value: "Muy Baja" },
    { label: "Baja", value: "Baja" },
    { label: "Media", value: "Media" },
    { label: "Alta", value: "Alta" },
    { label: "Muy Alta", value: "Muy Alta" },
  ]);
  const { user } = useAuth();
  const emp_codigo = user?.emp_codigo;
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchData() {
      try {
        const activosData = await getActivosPorEmpresa(emp_codigo);
        setActivos(
          activosData.map((item) => ({
            value: item.act_codigo,
            label: item.act_nombre,
          }))
        );

        const vulnerabilidadesData = await getVulnerabilidadPorEmpresa(
          emp_codigo
        );
        setVulnerabilidades(
          vulnerabilidadesData.map((item) => ({
            value: item.vul_codigo,
            label: item.vul_nombre,
          }))
        );

        const amenazasData = await getAmenazasPorEmpresa(emp_codigo);
        setAmenazas(
          amenazasData.map((item) => ({
            value: item.ame_codigo,
            label: item.ame_nombre,
          }))
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Error al cargar los datos.");
      }
    }
    fetchData();
  }, [emp_codigo]);

  const handleVulnerabilidadSelect = (selected) => {
    setRowData((prev) => ({
      ...prev,
      vulnerabilidad: selected,
      amenazas: [], // Reset amenazas when selecting a new vulnerabilidad
    }));
  };

  const handleAgregarAmenaza = () => {
    if (!rowData.vulnerabilidad) {
      toast.warn(
        "Debe seleccionar una vulnerabilidad antes de agregar amenazas."
      );
      return;
    }
    setRowData((prev) => ({
      ...prev,
      amenazas: [...prev.amenazas, { amenaza: null, frecuencia: null }],
    }));
  };

  const handleAmenazaSelect = (amenazaIndex, selected) => {
    setRowData((prev) => {
      const newAmenazas = [...prev.amenazas];
      newAmenazas[amenazaIndex].amenaza = selected;
      return { ...prev, amenazas: newAmenazas };
    });
  };

  const handleInputChange = (amenazaIndex, field, value) => {
    setRowData((prev) => {
      const newAmenazas = [...prev.amenazas];
      newAmenazas[amenazaIndex][field] = value;
      return { ...prev, amenazas: newAmenazas };
    });
  };

  const handleEliminarAmenaza = (amenazaIndex) => {
    if (rowData.amenazas.length <= 1) {
      toast.warn(
        "No puede eliminar la única amenaza. Debe haber al menos una amenaza."
      );
      return;
    }
    setRowData((prev) => {
      const newAmenazas = [...prev.amenazas];
      newAmenazas.splice(amenazaIndex, 1);
      return { ...prev, amenazas: newAmenazas };
    });
  };

  const handleGuardarClick = async () => {
    if (!rowData.vulnerabilidad || rowData.amenazas.length === 0) {
      toast.error(
        "Debe seleccionar una vulnerabilidad y al menos una amenaza."
      );
      return;
    }
    for (const amenaza of rowData.amenazas) {
      if (!amenaza.amenaza || !amenaza.frecuencia) {
        toast.error("Por favor, complete todos los campos antes de guardar.");
        return;
      }
    }

    try {
      await saveDataAmenaza();
      toast.success("Información guardada con éxito.");
      setRowData({ vulnerabilidad: null, amenazas: [] });
    } catch (error) {
      const mensaje =
        error?.message || "Error al guardar los datos. Intenta nuevamente.";
      toast.error(mensaje);
    }
  };

  const saveDataAmenaza = async () => {
    const dataToSend = rowData.amenazas
      .filter((amenaza) => amenaza.amenaza && amenaza.frecuencia)
      .map((amenaza) => ({
        payload: {
          vul_codigo: rowData.vulnerabilidad.value,
          ame_codigo: amenaza.amenaza.value,
          amv_probabilidad: amenaza.frecuencia,
          amv_fecha_inicio: new Date()
            .toISOString()
            .slice(0, 19)
            .replace("T", " "),
          amv_fecha_inactividad: null,
          amv_valor_dano_promedio: null,
        },
        meta: {
          vulnerabilidad: rowData.vulnerabilidad.label,
          amenaza: amenaza.amenaza.label,
        },
      }));

    if (dataToSend.length === 0) {
      throw new Error("No valid data to send.");
    }

    for (const { payload, meta } of dataToSend) {
      try {
        await createAmenazaVulnerabilidad(payload);
      } catch (error) {
        if (error?.response?.status === 409) {
          const mensaje =
            error?.response?.data?.message ||
            `La amenaza "${meta?.amenaza}" ya está asociada a la vulnerabilidad "${meta?.vulnerabilidad}".`;
          throw new Error(mensaje);
        }
        throw error;
      }
    }
  };

  const handleCancelarProceso = () => {
    setRowData({ vulnerabilidad: null, amenazas: [] });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3, backgroundColor: '#F4F6F8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#001F3F' }}>
            Asociar Vulnerabilidades a Amenazas
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: '#3A6B7E' }}
          >
            Volver
          </Button>
        </Box>
        <Typography variant="subtitle1" sx={{ color: '#555', mb: 4 }}>
          Seleccione una vulnerabilidad y asocie las amenazas correspondientes.
        </Typography>

        <Grid container spacing={4}>
          {/* Selección de Vulnerabilidad */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#001F3F' }}>
                1. Seleccionar Vulnerabilidad
              </Typography>
              <Autocomplete
                options={vulnerabilidades}
                getOptionLabel={(option) => option.label}
                value={rowData.vulnerabilidad}
                onChange={(event, newValue) => handleVulnerabilidadSelect(newValue)}
                disabled={rowData.amenazas.length > 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vulnerabilidad"
                    variant="outlined"
                    fullWidth
                    helperText={rowData.amenazas.length > 0 ? "No se puede cambiar la vulnerabilidad con amenazas agregadas" : "Seleccione la vulnerabilidad"}
                  />
                )}
              />
            </Paper>
          </Grid>

          {/* Gestión de Amenazas */}
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#001F3F' }}>
                  2. Asociar Amenazas
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAgregarAmenaza}
                  disabled={!rowData.vulnerabilidad}
                  sx={{
                    backgroundColor: '#3A6B7E',
                    '&:hover': { backgroundColor: '#2C5261' },
                    textTransform: 'none'
                  }}
                >
                  Agregar Amenaza
                </Button>
              </Box>

              {rowData.amenazas.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', border: '2px dashed #ccc', borderRadius: 2, color: '#777' }}>
                  <Typography>No hay amenazas asociadas. Seleccione una vulnerabilidad y haga clic en "Agregar Amenaza".</Typography>
                </Box>
              ) : (
                rowData.amenazas.map((amenaza, index) => (
                  <Paper key={index} elevation={1} sx={{ p: 2, mb: 2, borderLeft: '6px solid #d32f2f', borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={amenazas}
                          getOptionLabel={(option) => option.label}
                          value={amenaza.amenaza}
                          onChange={(event, newValue) => handleAmenazaSelect(index, newValue)}
                          renderInput={(params) => <TextField {...params} label="Seleccione Amenaza" variant="outlined" size="small" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={frecuenciaOptions}
                          getOptionLabel={(option) => option.label}
                          value={frecuenciaOptions.find(opt => opt.value === amenaza.frecuencia) || null}
                          onChange={(event, newValue) => handleInputChange(index, "frecuencia", newValue ? newValue.value : null)}
                          renderInput={(params) => <TextField {...params} label="Frecuencia" variant="outlined" size="small" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
                        <Tooltip title="Eliminar Amenaza">
                          <IconButton
                            onClick={() => handleEliminarAmenaza(index)}
                            color="error"
                            disabled={rowData.amenazas.length <= 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleCancelarProceso}
            sx={{
              borderColor: "#94a3b8",
              color: "#475569",
              "&:hover": { backgroundColor: "#f1f5f9" },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleGuardarClick}
            sx={{
              backgroundColor: '#001F3F',
              '&:hover': { backgroundColor: '#003366' },
              px: 4
            }}
          >
            Guardar Asociación
          </Button>
        </Box>
        <ToastContainer position="bottom-left" autoClose={3000} />
      </Paper>
    </Container>
  );
};
